import React from 'react';
import { createContext } from '@shared/utils/createContext';
import type {
  GetAllGridItemsQueryOptions,
  GetAllGridItemsQueryResult,
  GetAllGridItemsLazyQueryOptions,
  GetAllGridItemsLazyQueryResult,
  GetAllGridItemsQueryVariables,
} from '../../../../api/queries/getAllProductGridItems';
import {
  useGetCatalogProductGridItemsQuery,
  useGetCatalogProductGridItemsLazyQuery,
  useGetUniversalGridItemsQuery,
  useGetUniversalGridItemsLazyQuery,
} from '../../../../api/queries/getAllProductGridItems';
import { UniversalCatalogSearchMode, useUniversalSearchFeature } from '../../../../hooks/useUniversalSearchFeature';
import type { ProductUniversalSearchSourceLimit } from '@graphql/generated-base';

type SearchProductsProviderResult = {
  useGetGridItemsQuery: (options: GetAllGridItemsQueryOptions) => GetAllGridItemsQueryResult;
  useGetGridItemsLazyQuery: (options: GetAllGridItemsLazyQueryOptions) => GetAllGridItemsLazyQueryResult;
};

const SEARCH_PRODUCTS_CONTEXT_DEFAULT: SearchProductsProviderResult = {
  useGetGridItemsQuery: () => ({ loading: true, productList: [] }),
  useGetGridItemsLazyQuery: () => ({ loading: true, fallbackProductList: [], sendRequest: () => {} }),
};

const [Provider, useProvider] = createContext<SearchProductsProviderResult>({ name: 'SearchProducts', initialValue: SEARCH_PRODUCTS_CONTEXT_DEFAULT });

const CatalogSearchProductsProvider: React.FC = props => {
  const result: SearchProductsProviderResult = {
    useGetGridItemsQuery: useGetCatalogProductGridItemsQuery,
    useGetGridItemsLazyQuery: useGetCatalogProductGridItemsLazyQuery,
  };

  return <Provider value={result}>{props.children}</Provider>;
};

type UniversalSearchProductsProviderProps = {
  universalCatalogMeshSourceLimits?: ProductUniversalSearchSourceLimit[];
};

type ArgsWithVariables<TArgs> = TArgs & {
  variables: GetAllGridItemsQueryVariables;
};

const UniversalSearchProductsProvider: React.FC<UniversalSearchProductsProviderProps> = ({ children, universalCatalogMeshSourceLimits }) => {
  const argsWithLimits = <T,>(args: ArgsWithVariables<T>): ArgsWithVariables<T> => ({ ...args, variables: { ...args.variables, sourceLimits: universalCatalogMeshSourceLimits } });
  // intercepts graphql queries and adds source limits from the experiment
  const result: SearchProductsProviderResult = {
    useGetGridItemsQuery: props => useGetUniversalGridItemsQuery(argsWithLimits(props)),
    useGetGridItemsLazyQuery: props => {
      const result = useGetUniversalGridItemsLazyQuery(props);
      return {
        ...result,
        sendRequest: options => result.sendRequest(argsWithLimits(options)),
      };
    },
  };

  return <Provider value={result}>{children}</Provider>;
};

export const useSearchProductsProvider = useProvider;
export const SearchProductsProvider: React.FC = props => {
  const { universalCatalogSearchMode, universalCatalogMeshSourceLimits, loading } = useUniversalSearchFeature();

  if (loading) {
    return <Provider value={SEARCH_PRODUCTS_CONTEXT_DEFAULT}>{props.children}</Provider>;
  }
  if (universalCatalogSearchMode === UniversalCatalogSearchMode.meshedProductGrid) {
    return (
      <UniversalSearchProductsProvider
        {...props}
        universalCatalogMeshSourceLimits={universalCatalogMeshSourceLimits}
      />
    );
  }
  return <CatalogSearchProductsProvider {...props} />;
};
